import ApplicationController from '../application_controller';

const VALOR_CUANDO_ESTA_OCULTO = 'modal';
/**
 * Controlador de Stimulus para gestionar la visualización del tipo de visualización
 * en formularios de ítems diarios de calificaciones.
 *
 * Este controlador maneja la lógica de mostrar/ocultar el campo de tipo de visualización
 * basándose en el tipo de nota seleccionado y si tiene opciones configuradas.
 *
 * @class TipoVisualizacionController
 * @extends ApplicationController
 *
 * Elementos esperados en el DOM:
 * - Los elementos especificados en las configuraciones de selectores
 *
 * Configuración del controlador:
 * - data-selector-tipo-nota (OBLIGATORIO): Selector CSS para encontrar el select de tipo de nota
 * - data-selector-wrapper-visualizacion (OBLIGATORIO): Selector CSS para encontrar el wrapper
 *   del tipo de visualización
 * - data-valor-por-defecto: Valor por defecto para el select de tipo de visualización
 *
 * Los elementos del select deben tener los siguientes data attributes:
 * - data-tipo-nota: Tipo de nota (ej: 'texto', 'numerica')
 * - data-opciones-length: Número de opciones configuradas para el tipo de nota
 */
export default class extends ApplicationController {
  /**
   * Se ejecuta cuando el controlador se conecta al DOM.
   * Configura los event listeners y establece el estado inicial.
   */
  connect() {
    this.setupEventListeners();
    this.toggleTipoVisualizacion();
  }

  /**
   * Se ejecuta cuando el controlador se desconecta del DOM.
   * Limpia los event listeners para evitar memory leaks.
   */
  disconnect() {
    this.removeEventListeners();
  }

  /**
   * Configura los event listeners necesarios para el funcionamiento del controlador.
   * Busca los elementos en el DOM usando los selectores configurados y establece
   * el listener para el cambio de tipo de nota.
   *
   * @throws {Error} Si no se encuentran los elementos con los selectores configurados
   * @private
   */
  setupEventListeners() {
    const { selectorTipoNota, selectorWrapperVisualizacion } = this.element.dataset;

    this.tipoNotaSelect = this.element.querySelector(selectorTipoNota);
    this.tipoVisualizacionWrapper = this.element.querySelector(selectorWrapperVisualizacion);

    if (!this.tipoNotaSelect) {
      throw new Error(`No se encontró el elemento con el selector: ${selectorTipoNota}`);
    }

    if (!this.tipoVisualizacionWrapper) {
      throw new Error(`No se encontró el elemento con el selector: ${selectorWrapperVisualizacion}`);
    }

    this.boundTipoNotaChanged = this.tipoNotaChanged.bind(this);
    this.tipoNotaSelect.addEventListener('change', this.boundTipoNotaChanged);
  }

  /**
   * Elimina los event listeners configurados previamente.
   *
   * @private
   */
  removeEventListeners() {
    if (this.tipoNotaSelect && this.boundTipoNotaChanged) {
      this.tipoNotaSelect.removeEventListener('change', this.boundTipoNotaChanged);
    }
  }

  /**
   * Establece el valor por defecto del select de tipo de visualización.
   * Lee el valor desde el data attribute 'data-valor-por-defecto'.
   *
   * @private
   */
  setDefaultVisualizacionValue() {
    const tipoVisualizacionSelect = this.tipoVisualizacionWrapper.querySelector('select');
    if (!tipoVisualizacionSelect) return;

    const { valorPorDefecto } = this.element.dataset;

    if (valorPorDefecto) {
      // Verificar que el valor existe en las opciones del select
      const optionExists = Array.from(tipoVisualizacionSelect.options)
        .some((option) => option.value === valorPorDefecto);

      if (optionExists) {
        tipoVisualizacionSelect.value = valorPorDefecto;
      }
    }
  }

  /**
   * Maneja el evento de cambio en el select de tipo de nota.
   * Ejecuta la lógica para mostrar u ocultar el campo de tipo de visualización.
   *
   * @private
   */
  tipoNotaChanged() {
    this.toggleTipoVisualizacion();
  }

  /**
   * Controla la visibilidad del campo de tipo de visualización basándose en:
   * - El tipo de nota seleccionado debe ser 'texto'
   * - El tipo de nota debe tener opciones configuradas (opcionesLength > 0)
   *
   * Si ambas condiciones se cumplen, muestra el campo y establece el valor por defecto;
   * de lo contrario, lo oculta y limpia su valor.
   *
   * @private
   */
  toggleTipoVisualizacion() {
    if (!this.tipoNotaSelect || !this.tipoVisualizacionWrapper) return;

    const selectedOption = this.tipoNotaSelect.options[this.tipoNotaSelect.selectedIndex];
    const { tipoNota } = selectedOption.dataset;
    const opcionesLength = parseInt(selectedOption.dataset.opcionesLength, 10);

    const wasHidden = this.tipoVisualizacionWrapper.style.display === 'none';

    if (tipoNota === 'texto' && opcionesLength > 0) {
      this.tipoVisualizacionWrapper.style.display = '';

      // Si el campo estaba oculto y ahora se muestra, establecer valor por defecto
      if (wasHidden) {
        this.setDefaultVisualizacionValue();
      }
    } else {
      this.tipoVisualizacionWrapper.style.display = 'none';

      // Limpiar el valor del select cuando se oculta
      const tipoVisualizacionSelect = this.tipoVisualizacionWrapper.querySelector('select');
      if (tipoVisualizacionSelect) {
        tipoVisualizacionSelect.value = VALOR_CUANDO_ESTA_OCULTO;
      }
    }
  }
}
